/**
 * Componente banner de feedback inferior siempre visible
 * Permite a los usuarios calificar su experiencia en cualquier momento
 */

import { useState, useEffect } from 'react';
import './FeedbackBanner.css';

const FEEDBACK_SENT_KEY = 'feedback_sent_this_session';

interface FeedbackBannerProps {
  onSubmit: (rating: number, feedback: string) => Promise<void>;
  isVisible?: boolean; // Controlar visibilidad del banner
  onExpandChange?: (isExpanded: boolean) => void; // Callback cuando se expande/colapsa
}

export function FeedbackBanner({ onSubmit, isVisible = true, onExpandChange }: FeedbackBannerProps) {
  const [rating, setRating] = useState(0);
  const [hoveredRating, setHoveredRating] = useState(0);
  const [feedback, setFeedback] = useState('');
  const [isExpanded, setIsExpanded] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(() => {
    // Verificar al inicializar si ya se envió feedback en esta sesión
    return sessionStorage.getItem(FEEDBACK_SENT_KEY) === 'true';
  });

  // Verificar sessionStorage al montar
  useEffect(() => {
    if (sessionStorage.getItem(FEEDBACK_SENT_KEY) === 'true') {
      setIsSubmitted(true);
    }
  }, []);

  if (!isVisible || isSubmitted) return null;

  const handleStarClick = (value: number) => {
    setRating(value);
    // Expandir automáticamente si se selecciona una calificación
    if (!isExpanded) {
      setIsExpanded(true);
      onExpandChange?.(true);
    }
  };

  const handleStarHover = (value: number) => {
    setHoveredRating(value);
  };

  const handleStarLeave = () => {
    setHoveredRating(0);
  };

  const handleSubmit = async () => {
    if (rating === 0) {
      alert('Por favor selecciona una calificación');
      return;
    }

    setIsSubmitting(true);
    try {
      await onSubmit(rating, feedback);
      // Guardar en sessionStorage que ya se envió feedback en esta sesión
      sessionStorage.setItem(FEEDBACK_SENT_KEY, 'true');
      // Notificar que el banner se colapsa antes de ocultarlo
      setIsExpanded(false);
      onExpandChange?.(false);
      setIsSubmitted(true);
    } catch (error) {
      console.error('Error al enviar feedback:', error);
      alert('Error al enviar la retroalimentación. Por favor intenta de nuevo.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleToggleExpand = () => {
    const newExpandedState = !isExpanded;
    setIsExpanded(newExpandedState);
    onExpandChange?.(newExpandedState);
  };

  return (
    <div className={`feedback-banner ${isExpanded ? 'expanded' : ''}`}>
      <div className="feedback-banner-content">
        {!isExpanded ? (
          // Vista colapsada: solo pregunta y estrellas
          <div className="feedback-banner-collapsed">
            <span className="feedback-banner-question">¿Cómo fue tu experiencia?</span>
            <div className="feedback-banner-stars">
              {[1, 2, 3, 4, 5].map((value) => {
                const isFilled = value <= (hoveredRating || rating);
                return (
                  <button
                    key={value}
                    type="button"
                    className={`feedback-star-button ${isFilled ? 'filled' : 'empty'}`}
                    onClick={() => handleStarClick(value)}
                    onMouseEnter={() => handleStarHover(value)}
                    onMouseLeave={handleStarLeave}
                    aria-label={`Calificar ${value} estrella${value > 1 ? 's' : ''}`}
                  >
                    {isFilled ? '⭐' : '☆'}
                  </button>
                );
              })}
            </div>
            {rating > 0 && (
              <button
                className="feedback-expand-button"
                onClick={handleToggleExpand}
                aria-label="Expandir para agregar comentarios"
                title="Agregar comentarios"
              >
                💬
              </button>
            )}
          </div>
        ) : (
          // Vista expandida: estrellas, campo de texto y botón enviar
          <div className="feedback-banner-expanded">
            <div className="feedback-banner-header">
              <span className="feedback-banner-question">¿Cómo fue tu experiencia?</span>
              <button
                className="feedback-collapse-button"
                onClick={handleToggleExpand}
                aria-label="Colapsar"
              >
                ×
              </button>
            </div>
            
            <div className="feedback-banner-stars">
              {[1, 2, 3, 4, 5].map((value) => {
                const isFilled = value <= (hoveredRating || rating);
                return (
                  <button
                    key={value}
                    type="button"
                    className={`feedback-star-button ${isFilled ? 'filled' : 'empty'}`}
                    onClick={() => handleStarClick(value)}
                    onMouseEnter={() => handleStarHover(value)}
                    onMouseLeave={handleStarLeave}
                    aria-label={`Calificar ${value} estrella${value > 1 ? 's' : ''}`}
                  >
                    {isFilled ? '⭐' : '☆'}
                  </button>
                );
              })}
            </div>
            {rating > 0 && (
              <p className="feedback-rating-text">
                {rating === 1 && 'Muy mala'}
                {rating === 2 && 'Mala'}
                {rating === 3 && 'Regular'}
                {rating === 4 && 'Buena'}
                {rating === 5 && 'Excelente'}
              </p>
            )}

            <div className="feedback-text-section">
              <textarea
                className="feedback-textarea"
                placeholder="Comparte tus comentarios, sugerencias o experiencias (opcional)..."
                value={feedback}
                onChange={(e) => setFeedback(e.target.value)}
                rows={3}
                maxLength={1000}
              />
              <span className="feedback-char-count">{feedback.length}/1000</span>
            </div>

            <div className="feedback-banner-actions">
              <button
                className="feedback-submit-button"
                onClick={handleSubmit}
                disabled={isSubmitting || rating === 0}
              >
                {isSubmitting ? 'Enviando...' : 'Enviar y cerrar'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
